export interface RecurrenceTranslations {
  dayNames: { [key: string]: string };
  monthNames: { [key: string]: string };
  positionNames: { [key: string]: string };
  recurrencePatterns: {
    everyDay: string;
    everyDays: string;
    everyWeekOn: string;
    everyWeeksOn: string;
    everyMonthOnDay: string;
    everyMonthsOnDay: string;
    everyMonthOnThe: string;
    everyMonthsOnThe: string;
    everyYearOn: string;
    everyYearsOn: string;
    everyYearOnThe: string;
    everyYearsOnThe: string;
    noRepetition: string;
    and: string;
  };
}

// Default English translations (fallback)
const defaultTranslations: RecurrenceTranslations = {
  dayNames: {
    MO: 'Monday',
    TU: 'Tuesday',
    WE: 'Wednesday',
    TH: 'Thursday',
    FR: 'Friday',
    SA: 'Saturday',
    SU: 'Sunday',
  },
  monthNames: {
    '1': 'January',
    '2': 'February',
    '3': 'March',
    '4': 'April',
    '5': 'May',
    '6': 'June',
    '7': 'July',
    '8': 'August',
    '9': 'September',
    '10': 'October',
    '11': 'November',
    '12': 'December',
  },
  positionNames: {
    '1': 'First',
    '2': 'Second',
    '3': 'Third',
    '4': 'Fourth',
    '-1': 'Last',
  },
  recurrencePatterns: {
    everyDay: 'Every {interval} day',
    everyDays: 'Every {interval} days',
    everyWeekOn: 'Every {interval} week on {days}',
    everyWeeksOn: 'Every {interval} weeks on {days}',
    everyMonthOnDay: 'Every {interval} month on day {day}',
    everyMonthsOnDay: 'Every {interval} months on day {day}',
    everyMonthOnThe: 'Every {interval} month on the {position} {day}',
    everyMonthsOnThe: 'Every {interval} months on the {position} {day}',
    everyYearOn: 'Every {interval} year on {month} {day}',
    everyYearsOn: 'Every {interval} years on {month} {day}',
    everyYearOnThe: 'Every {interval} year on the {position} {day} of {month}',
    everyYearsOnThe: 'Every {interval} years on the {position} {day} of {month}',
    noRepetition: 'No repetition',
    and: 'and',
  },
};

export const repeatRuleToText = (rrule: string, translations?: RecurrenceTranslations): string | null => {
  if (!rrule) return '';

  const t = translations || defaultTranslations;
  const { dayNames, monthNames, positionNames, recurrencePatterns } = t;

  const values = rrule.replace('RRULE:', '').split(';');

  let repeatType = '';
  let repeatEvery = 1;
  let selectedDays: string[] = [];
  let selectedDay = '';
  let selectedMonth = 1;
  let positions: string[] = [];
  const hasPosition = rrule.includes('BYSETPOS');
  let humanReadableString = '';

  values.forEach((value) => {
    const [key, val] = value.split('=');
    switch (key) {
      case 'FREQ':
        repeatType = val;
        break;
      case 'INTERVAL':
        repeatEvery = parseInt(val, 10);
        break;
      case 'BYDAY':
        selectedDays = val.split(',');
        break;
      case 'BYMONTHDAY':
        selectedDay = val;
        break;
      case 'BYMONTH':
        selectedMonth = parseInt(val, 10);
        break;
      case 'BYSETPOS':
        positions = val.split(',');
        break;
    }
  });

  const formatPattern = (pattern: string, replacements: { [key: string]: string | number }): string => {
    let result = pattern;
    for (const [key, value] of Object.entries(replacements)) {
      result = result.replace(`{${key}}`, String(value));
    }
    return result;
  };

  switch (repeatType) {
    case 'DAILY':
      humanReadableString = formatPattern(
        repeatEvery > 1 ? recurrencePatterns.everyDays : recurrencePatterns.everyDay,
        { interval: repeatEvery || '' }
      );
      break;
    case 'WEEKLY':
      const days = selectedDays.map((day) => dayNames[day] || day).join(', ');
      humanReadableString = formatPattern(
        repeatEvery > 1 ? recurrencePatterns.everyWeeksOn : recurrencePatterns.everyWeekOn,
        { interval: repeatEvery || '', days }
      );
      break;
    case 'MONTHLY':
      if (hasPosition) {
        if (positions.length > 1) {
          // Handle multiple positions (e.g., "Second and last Thursday")
          const positionStrings = positions.map((pos) => positionNames[pos.trim()] || pos);
          const lastPosition = positionStrings.pop();
          const positionsText =
            positionStrings.length > 0 ? positionStrings.join(', ') + ` ${recurrencePatterns.and} ` + lastPosition : lastPosition;
          humanReadableString = formatPattern(
            repeatEvery > 1 ? recurrencePatterns.everyMonthsOnThe : recurrencePatterns.everyMonthOnThe,
            { interval: repeatEvery || '', position: positionsText || '', day: dayNames[selectedDays[0]] || '' }
          );
        } else {
          // Check if BYDAY contains combined position+day format (e.g., "1MO")
          const firstDay = selectedDays[0];
          if (firstDay && firstDay.length > 2) {
            // Extract position and day from combined format like "1MO"
            const position = firstDay.substring(0, firstDay.length - 2);
            const day = firstDay.substring(firstDay.length - 2);
            humanReadableString = formatPattern(
              repeatEvery > 1 ? recurrencePatterns.everyMonthsOnThe : recurrencePatterns.everyMonthOnThe,
              { interval: repeatEvery || '', position: positionNames[position] || position, day: dayNames[day] || day }
            );
          } else {
            // Fallback to using BYSETPOS and BYDAY separately
            humanReadableString = formatPattern(
              repeatEvery > 1 ? recurrencePatterns.everyMonthsOnThe : recurrencePatterns.everyMonthOnThe,
              { interval: repeatEvery || '', position: positionNames[positions[0]] || '', day: dayNames[selectedDays[0]] || '' }
            );
          }
        }
      } else {
        humanReadableString = formatPattern(
          repeatEvery > 1 ? recurrencePatterns.everyMonthsOnDay : recurrencePatterns.everyMonthOnDay,
          { interval: repeatEvery || '', day: selectedDay || '' }
        );
      }
      break;
    case 'YEARLY':
      if (hasPosition) {
        // Check if BYDAY contains combined position+day format (e.g., "1MO")
        const firstDay = selectedDays[0];
        if (firstDay && firstDay.length > 2) {
          // Extract position and day from combined format like "1MO"
          const position = firstDay.substring(0, firstDay.length - 2);
          const day = firstDay.substring(firstDay.length - 2);
          humanReadableString = formatPattern(
            repeatEvery > 1 ? recurrencePatterns.everyYearsOnThe : recurrencePatterns.everyYearOnThe,
            { interval: repeatEvery || '', position: positionNames[position] || position, day: dayNames[day] || day, month: monthNames[String(selectedMonth)] || '' }
          );
        } else {
          // Fallback to using BYSETPOS and BYDAY separately
          humanReadableString = formatPattern(
            repeatEvery > 1 ? recurrencePatterns.everyYearsOnThe : recurrencePatterns.everyYearOnThe,
            { interval: repeatEvery || '', position: positionNames[positions[0]] || '', day: dayNames[selectedDays[0]] || '', month: monthNames[String(selectedMonth)] || '' }
          );
        }
      } else {
        humanReadableString = formatPattern(
          repeatEvery > 1 ? recurrencePatterns.everyYearsOn : recurrencePatterns.everyYearOn,
          { interval: repeatEvery || '', month: monthNames[String(selectedMonth)] || '', day: selectedDay || '' }
        );
      }
      break;
    default:
      humanReadableString = recurrencePatterns.noRepetition;
      break;
  }

  return humanReadableString;
};
